import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    Box,
    IconButton,
    Typography,
    Alert,
    Chip,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import BugReportIcon from "@mui/icons-material/BugReport";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import FavoriteIcon from "@mui/icons-material/Favorite";
import HelpIcon from "@mui/icons-material/Help";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import FeedbackIcon from "@mui/icons-material/Feedback";
import { SubmitFeedback } from "../FeedbackData.ts";
import { toast } from "react-toastify";

const FeedbackDialog = ({ open, onClose }) => {
    const [formData, setFormData] = useState({
        category: "",
        subject: "",
        details: "",
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isFormValid, setIsFormValid] = useState(false);

    const categories = [
        {
            value: "Issue",
            label: "Issue",
            icon: BugReportIcon,
            color: "#f44336",
            description: "Report a bug or technical problem"
        },
        {
            value: "Suggestion",
            label: "Suggestion",
            icon: LightbulbIcon,
            color: "#ff9800",
            description: "Share ideas for improvements"
        },
        {
            value: "Compliment",
            label: "Compliment",
            icon: FavoriteIcon,
            color: "#4caf50",
            description: "Share positive feedback"
        },
        {
            value: "Question",
            label: "Question",
            icon: HelpIcon,
            color: "#2196f3",
            description: "Ask for help or clarification"
        },
        {
            value: "Other",
            label: "Other",
            icon: MoreHorizIcon,
            color: "#9c27b0",
            description: "General feedback or comments"
        }
    ];

    // Form validation effect
    useEffect(() => {
        const validateForm = () => {
            const isCategoryValid = formData.category !== "";
            const isSubjectValid = formData.subject.trim().length >= 1 && formData.subject.length <= 100;
            const isDetailsValid = formData.details.trim().length >= 10;

            setIsFormValid(isCategoryValid && isSubjectValid && isDetailsValid);
        };

        validateForm();
    }, [formData]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ""
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.category) {
            newErrors.category = "Category is required";
        }

        if (!formData.subject.trim()) {
            newErrors.subject = "Subject is required";
        } else if (formData.subject.length > 100) {
            newErrors.subject = "Subject must be 100 characters or less";
        }

        if (!formData.details.trim()) {
            newErrors.details = "Details are required";
        } else if (formData.details.length < 10) {
            newErrors.details = "Details must be at least 10 characters";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            await SubmitFeedback({
                Category: formData.category,
                Subject: formData.subject.trim(),
                Details: formData.details.trim(),
            });

            toast.success("Feedback submitted successfully!", {
                position: "top-center",
                autoClose: 3000,
            });

            handleClose();
        } catch (error) {
            toast.error(error.message || "Failed to submit feedback", {
                position: "top-center",
                autoClose: 5000,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        setFormData({
            category: "",
            subject: "",
            details: "",
        });
        setErrors({});
        setIsSubmitting(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                        <FeedbackIcon color="primary" />
                        <Typography variant="h6">Provide Feedback</Typography>
                    </Box>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                {/* Explanation Section */}
                <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Your feedback helps us improve!
                    </Typography>
                    <Typography variant="body2">
                        Please select a category that best describes your feedback and provide detailed information.
                        Your input is valuable and will be reviewed by our team to enhance your experience.
                    </Typography>
                </Alert>

                <Box sx={{ mt: 2 }}>
                    <FormControl fullWidth margin="normal" error={!!errors.category}>
                        <InputLabel>Category</InputLabel>
                        <Select
                            value={formData.category}
                            label="Category"
                            onChange={(e) => handleInputChange("category", e.target.value)}
                            renderValue={(selected) => {
                                const category = categories.find(cat => cat.value === selected);
                                if (!category) return selected;
                                const IconComponent = category.icon;
                                return (
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <IconComponent sx={{ color: category.color, fontSize: 20 }} />
                                        <Typography>{category.label}</Typography>
                                    </Box>
                                );
                            }}
                        >
                            {categories.map((category) => {
                                const IconComponent = category.icon;
                                return (
                                    <MenuItem key={category.value} value={category.value}>
                                        <ListItemIcon>
                                            <IconComponent sx={{ color: category.color }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={category.label}
                                            secondary={category.description}
                                        />
                                    </MenuItem>
                                );
                            })}
                        </Select>
                        {errors.category && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {errors.category}
                            </Typography>
                        )}
                    </FormControl>

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Subject"
                        value={formData.subject}
                        onChange={(e) => handleInputChange("subject", e.target.value)}
                        error={!!errors.subject}
                        helperText={errors.subject || `${formData.subject.length}/100 characters`}
                        inputProps={{ maxLength: 100 }}
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        label="Details"
                        multiline
                        rows={4}
                        value={formData.details}
                        onChange={(e) => handleInputChange("details", e.target.value)}
                        error={!!errors.details}
                        helperText={errors.details || `${formData.details.length} characters (minimum 10)`}
                        placeholder="Please provide detailed feedback..."
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 3, gap: 2 }}>
                <LoadingButton
                    onClick={handleClose}
                    disabled={isSubmitting}
                    variant="outlined"
                    color="inherit"
                >
                    Cancel
                </LoadingButton>
                <LoadingButton
                    variant="contained"
                    onClick={handleSubmit}
                    loading={isSubmitting}
                    disabled={!isFormValid}
                    startIcon={<FeedbackIcon />}
                    loadingPosition="start"
                >
                    Submit Feedback
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackDialog;
